import React, {
  Fragment,
  useState,
  useEffect,
  forwardRef,
  useRef,
  useContext,
  useMemo,
} from "react";
import clsx from "clsx";
import moment from "moment";
import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";
import Legend from "components/QueryBuilder/Elements/Legend";

import {
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import {
  MAIN_COLORS,
  selectFormat,
  StandardTooltip,
  truncateToDecimals,
} from "components/Utils/Utils.js";

import {
  formatQueryMetric,
  formatDate,
} from "components/QueryBuilder/Utils/QueryBuilderUtils";
import QueryBuilderXAxis from "components/QueryBuilder/Elements/QueryBuilderXAxis";
import {
  containsToolTipData,
  excludePreditionMetrics,
} from "components/Utils/ChartsDataUtils";

import { formatDataForChart } from "components/Utils/ChartsDataUtils";

const QueryBuilderStackedBarChart = forwardRef((props, ref) => {
  const { state: globalState } = useContext(QueryBuilderContext);

  // Trended Views
  let { chartData, all_dim_keys, dimensionsMap } = useMemo(
    () => formatDataForChart(props.dataGroupedByDate),
    [props.dataGroupedByDate]
  );

  /**
   * Removes all data which contains prediction
   */
  chartData = useMemo(
    () =>
      chartData.filter(
        // ignore elements which contains predictions
        (d) =>
          Object.keys(d).filter((key) => key.indexOf(".pred") >= 0).length === 0
      ),
    [chartData]
  );

  const [singleTooltipData, setTooltipData] = useState(null);
  const [seriesFilter, setSeriesFilter] = useState(null);
  const [state, setState] = useState({
    colors: {},
    lData: [],
  });

  const refChart = useRef(null);

  const [counterLoadMore, setCounterLoadMore] = useState(1);
  const LOAD_MORE_VALUE = 50;

  const { lData, colors } = state;

  useEffect(() => {
    const lData = [
      {
        name: "metrics",
        friendlyName: "Metrics",
        value: [
          {
            name: "select_all",
            friendlyName: "Select all",
            color: "#8a9ca3",
            checked: true,
          },
        ],
      },
    ];

    Object.keys(props.metricMap)
      .filter(excludePreditionMetrics)
      .forEach((metric) =>
        lData[0].value.push({
          name: props.metricMap[metric].name,
          friendlyName: props.metricMap[metric].friendlyName,
          color: "#38474d",
          checked: true,
        })
      );

    const colors = {};
    props.metrics.filter(excludePreditionMetrics).forEach((metric, j) => {
      const metrics = [
        {
          name: "select_all",
          friendlyName: "Select all",
          color: "#8a9ca3",
          checked: true,
        },
      ];

      if (all_dim_keys && all_dim_keys.length) {
        all_dim_keys.forEach((dim_key, i) => {
          colors[metric + ":" + dim_key] =
            MAIN_COLORS[(j * all_dim_keys.length + i) % 20];
          metrics.push({
            name: metric + ":" + dim_key,
            friendlyName: dim_key
              .split(":")
              .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
              .join(" | "),
            color: colors[metric + ":" + dim_key],
            checked: true,
          });
        });

        lData.push({
          name: props.metricMap[metric].name,
          friendlyName: props.metricMap[metric].friendlyName,
          value: metrics,
        });
      } else {
        colors[metric] = MAIN_COLORS[j % 20];
        lData[0].value.find((d) => d.name === metric).color = colors[metric];
      }
    });

    setState({
      ...state,
      colors,
      lData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderBars = () => {
    let bars = [];
    let isFixedBarWidth = null;

    props.metrics
      // ignores the metrics that comes from the prediction
      .filter(excludePreditionMetrics)
      .forEach((metric, j) => {
        if (all_dim_keys && all_dim_keys.length) {
          // metrics + dimensions
          all_dim_keys.forEach((dim_key, i) => {
            if (
              !seriesFilter ||
              (seriesFilter && seriesFilter.includes(metric + ":" + dim_key))
            ) {
              isFixedBarWidth = all_dim_keys.length <= 7;
              bars.push(
                <Bar
                  key={`bar_${metric + ":" + dim_key}`}
                  dataKey={metric + ":" + dim_key}
                  fill={`${colors[metric + ":" + dim_key]}`}
                  barSize={isFixedBarWidth ? 35 : null}
                  stackId={metric}
                  onMouseOver={(d, e) => {
                    setTooltipData(d);
                  }}
                  onMouseOut={() => setTooltipData(null)}
                />
              );
            }
          });
        } else {
          // only metrics
          if (
            !seriesFilter ||
            (seriesFilter && seriesFilter.includes(metric))
          ) {
            isFixedBarWidth = all_dim_keys.length <= 7;
            bars.push(
              <Bar
                key={`bar_${metric}`}
                dataKey={metric}
                fill={`${colors[metric]}`}
                barSize={isFixedBarWidth ? 35 : null}
                stackId={metric}
                onMouseOver={(d) => setTooltipData(d)}
                onMouseOut={() => setTooltipData(null)}
              />
            );
          }
        }
      });

    return bars;
  };

  const allBars = renderBars();
  const numberOfBars = LOAD_MORE_VALUE * counterLoadMore;

  const onChangeSelection = (selection) => {
    const checkedMetrics = selection.metrics
      .slice(1)
      .filter((d) => d.checked)
      .map((d) => d.name);
    const _seriesFilter = checkedMetrics.slice(0);
    checkedMetrics.forEach(
      (m) =>
        selection[m] &&
        selection[m]
          .slice(1)
          .forEach((d) => d.checked && _seriesFilter.push(d.name))
    );
    setSeriesFilter(_seriesFilter);
  };

  const chartWidth = refChart.current ? refChart.current.offsetWidth / 43 : "";
  const isDateRangeGreaterThanYear = moment
    .utc(props.startDate)
    .diff(moment.utc(props.endDate), "years");

  return (
    <div className="row">
      <div
        ref={refChart}
        className="col-10 query-view-charts query-view-border pt-4 pb-4 pl-3 pr-3"
      >
        {allBars.length > numberOfBars ? (
          <div className="query-view-load-more mr-3 d-flex align-items-baseline">
            <span>{numberOfBars} items displayed</span>
            <button
              className="btn btn-link nav__item nav__item--sm hidden-md-down ml-3"
              onClick={() => setCounterLoadMore(counterLoadMore + 1)}
            >
              Load more
            </button>
          </div>
        ) : (
          ""
        )}

        <ResponsiveContainer height={500} width="100%">
          <BarChart ref={ref} data={chartData} barCategoryGap={0}>
            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />
            {allBars.slice(
              0,
              allBars.length > numberOfBars ? numberOfBars : allBars.length
            )}
            <YAxis
              axisLine={false}
              tickLine={false}
              mirror={true}
              tickFormatter={(d) =>
                props.formatType ? selectFormat(props.formatType, d) : d
              }
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              interval={
                isDateRangeGreaterThanYear
                  ? 6
                  : chartData.length < chartWidth
                  ? 0
                  : Math.round(chartData.length / chartWidth)
              }
              minTickGap={20}
              padding={{ left: 20, right: 20 }}
              tick={<QueryBuilderXAxis granularity={props.granularity} />}
            />
            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={
                <StandardTooltip
                  render={() => {
                    if (singleTooltipData) {
                      const arrNames = Object.keys(singleTooltipData.payload);
                      const value =
                        singleTooltipData.value[1] - singleTooltipData.value[0];
                      const dataKey = arrNames.find(
                        (el) =>
                          (!isNaN(singleTooltipData.payload[el]) &&
                            truncateToDecimals(
                              singleTooltipData.payload[el],
                              5
                            ) === truncateToDecimals(+value, 5)) ||
                          singleTooltipData.payload[el] === +value
                      );

                      const tooltipData = {
                        date: singleTooltipData.payload.date,
                        [dataKey]: value,
                      };

                      const currentDatePoint = tooltipData["date"];

                      const renderItem = (index, parent) => {
                        const dim = props.dimensions[index];

                        return dim.values.map((dim_key, i) => {
                          const dimFriendlyName = dimensionsMap
                            .map((dimensions) =>
                              dimensions.find((dim) => dim.name === dim_key)
                            )
                            .filter((o) => o !== undefined)[0]?.friendlyName;

                          const full_key = parent + ":" + dimFriendlyName;
                          const color = colors[full_key];

                          return containsToolTipData(
                            full_key,
                            seriesFilter,
                            tooltipData
                          ) ? (
                            index < props.dimensions.length - 1 ? (
                              <Fragment key={full_key}>
                                <div
                                  className="standard-tooltip__item"
                                  style={{
                                    marginLeft: (index + 1) * 15 + "px",
                                  }}
                                >
                                  <span
                                    className={clsx(
                                      "standard-tooltip__key",
                                      "text-capitalize"
                                    )}
                                  >
                                    {dim_key}
                                  </span>
                                  <br />
                                </div>
                                {renderItem(index + 1, full_key)}
                              </Fragment>
                            ) : (
                              <div
                                className="standard-tooltip__item"
                                key={full_key}
                                style={{ marginLeft: (index + 1) * 5 + "px" }}
                              >
                                <span
                                  className="standard-tooltip__icon"
                                  style={{ background: color }}
                                />
                                <span
                                  className={clsx(
                                    "standard-tooltip__key",
                                    "text-capitalize"
                                  )}
                                  style={{ color: color, maxWidth: 125 }}
                                >
                                  {dim_key}
                                </span>
                                <br />
                                <span className="standard-tooltip__value">
                                  {formatQueryMetric(
                                    parent,
                                    singleTooltipData.payload[full_key],
                                    globalState.queryMetrics
                                  )}
                                </span>
                              </div>
                            )
                          ) : null;
                        });
                      };
                      return (
                        <div className="standard-tooltip">
                          {tooltipData && (
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__key-header">
                                Date:{" "}
                                <strong>
                                  {moment(new Date(currentDatePoint))
                                    .utc()
                                    .format(
                                      formatDate(props.granularity.value, true)
                                    )}
                                </strong>
                              </span>
                            </header>
                          )}
                          <main className="standard-tooltip__main">
                            {props.metrics.map((metric, j) => {
                              return containsToolTipData(
                                metric,
                                seriesFilter,
                                tooltipData
                              ) ? (
                                all_dim_keys.length === 0 ? (
                                  <div
                                    className="standard-tooltip__item"
                                    key={j}
                                  >
                                    <span
                                      className="standard-tooltip__icon"
                                      style={{ background: colors[metric] }}
                                    />
                                    <span
                                      className={clsx(
                                        "standard-tooltip__key",
                                        "text-capitalize"
                                      )}
                                      style={{ color: colors[metric] }}
                                    >
                                      {props.metricMap[metric].friendlyName}
                                    </span>
                                    <br />
                                    <span className="standard-tooltip__value">
                                      {formatQueryMetric(
                                        metric,
                                        tooltipData[metric],
                                        globalState.queryMetrics
                                      )}
                                    </span>
                                  </div>
                                ) : (
                                  <Fragment key={metric}>
                                    <div className="standard-tooltip__item">
                                      <span
                                        className={clsx(
                                          "standard-tooltip__key",
                                          "text-capitalize"
                                        )}
                                      >
                                        {props.metricMap[metric].friendlyName}
                                      </span>
                                    </div>
                                    {renderItem(0, metric)}
                                  </Fragment>
                                )
                              ) : null;
                            })}
                          </main>
                        </div>
                      );
                    }
                  }}
                />
              }
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="col-2">
        <Legend data={lData} onChangeSelection={onChangeSelection} />
      </div>
    </div>
  );
});

export default QueryBuilderStackedBarChart;
